import React from 'react';
import {sb} from '../lib/supabase.js';
import {queuedReportActor} from '../lib/queuedReportApi.js';
import {lastAuthenticatedUserId} from '../lib/authIdentityMemory.js';
import {forgetSave, reportSaveFailed, reportSaved, reportSaving} from '../lib/saveStatus.js';
import {useAppDraftGuard} from '../app/navigation/index.js';
import InlineNotice from '../shared/InlineNotice.jsx';
import Button from '../shared/Button.jsx';
import type {AddFeedDraftForm, AddFeedDraftSession} from './addFeedDraftSession.js';

// The Add Feed form's unfinished draft. This shim is all of it that ships in
// the startup bundle: the session (addFeedDraftSession.ts) loads on demand and
// is handed the startup modules it uses (see that file's header).
const loadSession = () => import('./addFeedDraftSession.js');
const DraftView = React.lazy(() => loadSession().then((m) => ({default: m.AddFeedDraftView})));
const DEPS = {
  React,
  useGuard: useAppDraftGuard,
  getActor: () => queuedReportActor(sb),
  liveAccount: lastAuthenticatedUserId,
  status: {reportSaving, reportSaved, reportSaveFailed, forgetSave},
  ui: {InlineNotice, Button},
};

export function useAddFeedDraft(form: AddFeedDraftForm['current']) {
  const [session, setSession] = React.useState<AddFeedDraftSession>();
  const live = React.useRef(form);
  live.current = form;
  React.useEffect(() => {
    let alive = true;
    let opened: AddFeedDraftSession | undefined;
    loadSession().then(
      (m) => alive && setSession((opened = m.openAddFeedDraft(live, DEPS))),
      () => {},
    );
    return () => {
      alive = false;
      opened?.close();
    };
  }, []);
  return {
    view: session && React.createElement(React.Suspense, {fallback: null}, React.createElement(DraftView, {session})),
    // Log Feed stays the only send; only a confirmed send clears the draft.
    beforeSend: () => session?.beforeSend(),
    afterSend: (confirmed: boolean) => session?.afterSend(confirmed),
  };
}
